/**
 * talking-avatar/face-panel.js — manual FACE / expression + head-pose control panel.
 *
 * Sits on top of the talking-avatar engine's window.__taMorph API (app.js). Lets the
 * user dial an expression by hand (presets + semantic sliders + a raw "all params"
 * panel with GNM-like granularity) and pose the head. Whatever the user sets becomes
 * the manual BASE pose — lip-sync is added on top, so a posed face can still talk, and
 * the pose is baked into the exported video.
 *
 * Single source of truth: `morphState` (lowerName → 0..1) + `pose` (rx,ry,rz radians).
 * Every control (semantic, raw, preset, pose) writes that state, then apply() pushes it
 * to __taMorph and syncUI() reflects it back into every slider (so overlapping controls
 * — e.g. the "Smile" macro and the raw mouthSmileLeft slider — stay consistent).
 *
 * Empirical basis (morph-probe-scratch.mjs, 2026-07-16): all 66 ARKit/Oculus morphs on
 * mpfb.glb deform correctly; mouth/jaw are the strongest, eyes/brows are real but small
 * on screen, tongueOut only reads with the mouth open (hence the hint).
 */
(function () {
    'use strict';

    var F = (typeof window !== 'undefined' && window.__TA_FACE) || {};
    function t(path, fb) {
        try { var o = F, p = path.split('.'); for (var i = 0; i < p.length; i++) { o = o[p[i]]; if (o == null) return fb; } return o; }
        catch (_) { return fb; }
    }

    var morphState = {};                 // lowerName -> 0..1
    var pose = { rx: 0, ry: 0, rz: 0 };  // head rotation (radians)
    var controls = [];                   // {kind, el, read(), write(v)} descriptors for syncUI
    var built = false;

    var POSE_MAX = { rx: 0.5, ry: 0.6, rz: 0.5 };  // slider ±100 → ± radians (~±29°/34°/29°)

    // ── semantic controls: friendly sliders mapping to one or more raw morphs ──
    // uni: 0..100 → each name = v/100.   bi: -100..100 → pos side or neg side.
    var SEMANTIC = [
        { group: 'mouth', kind: 'uni', key: 'mouth_open', names: ['jawOpen'] },
        { group: 'mouth', kind: 'uni', key: 'mouth_smile', names: ['mouthSmileLeft', 'mouthSmileRight'] },
        { group: 'mouth', kind: 'uni', key: 'mouth_frown', names: ['mouthFrownLeft', 'mouthFrownRight'] },
        { group: 'mouth', kind: 'uni', key: 'mouth_pucker', names: ['mouthPucker'] },
        { group: 'mouth', kind: 'uni', key: 'mouth_funnel', names: ['mouthFunnel'] },
        { group: 'mouth', kind: 'bi', key: 'jaw_h', neg: ['jawLeft'], pos: ['jawRight'] },
        { group: 'eyes', kind: 'uni', key: 'eyes_close', names: ['eyeBlinkLeft', 'eyeBlinkRight'] },
        { group: 'eyes', kind: 'uni', key: 'eyes_wide', names: ['eyeWideLeft', 'eyeWideRight'] },
        { group: 'eyes', kind: 'uni', key: 'eyes_squint', names: ['eyeSquintLeft', 'eyeSquintRight'] },
        { group: 'eyes', kind: 'bi', key: 'eyes_h', neg: ['eyeLookOutLeft', 'eyeLookInRight'], pos: ['eyeLookInLeft', 'eyeLookOutRight'] },
        { group: 'eyes', kind: 'bi', key: 'eyes_v', neg: ['eyeLookDownLeft', 'eyeLookDownRight'], pos: ['eyeLookUpLeft', 'eyeLookUpRight'] },
        { group: 'brows', kind: 'uni', key: 'brows_up', names: ['browInnerUp', 'browOuterUpLeft', 'browOuterUpRight'] },
        { group: 'brows', kind: 'uni', key: 'brows_down', names: ['browDownLeft', 'browDownRight'] },
        { group: 'cheeks', kind: 'uni', key: 'cheeks_puff', names: ['cheekPuff'] },
        { group: 'cheeks', kind: 'uni', key: 'nose_sneer', names: ['noseSneerLeft', 'noseSneerRight'] },
        { group: 'tongue', kind: 'uni', key: 'tongue_out', names: ['tongueOut'], hint: true }
    ];

    // ── presets: raw morph maps (+ optional pose) for one-tap expressions ──
    var PRESETS = [
        { key: 'neutral', emoji: '😐', m: {} },
        { key: 'smile', emoji: '🙂', m: { mouthSmileLeft: 0.85, mouthSmileRight: 0.85, cheekSquintLeft: 0.4, cheekSquintRight: 0.4 } },
        { key: 'grin', emoji: '😄', m: { jawOpen: 0.18, mouthSmileLeft: 1, mouthSmileRight: 1, cheekSquintLeft: 0.55, cheekSquintRight: 0.55, eyeSquintLeft: 0.25, eyeSquintRight: 0.25 } },
        { key: 'surprise', emoji: '😮', m: { jawOpen: 0.5, eyeWideLeft: 1, eyeWideRight: 1, browInnerUp: 0.85, browOuterUpLeft: 0.7, browOuterUpRight: 0.7 } },
        { key: 'sad', emoji: '😔', m: { mouthFrownLeft: 0.7, mouthFrownRight: 0.7, browInnerUp: 0.85, mouthLowerDownLeft: 0.2, mouthLowerDownRight: 0.2 } },
        { key: 'angry', emoji: '😠', m: { browDownLeft: 1, browDownRight: 1, mouthPressLeft: 0.5, mouthPressRight: 0.5, noseSneerLeft: 0.4, noseSneerRight: 0.4, jawForward: 0.2 } },
        { key: 'wink', emoji: '😉', m: { eyeBlinkLeft: 1, mouthSmileLeft: 0.6, mouthSmileRight: 0.3, cheekSquintLeft: 0.5 } },
        { key: 'kiss', emoji: '😗', m: { mouthPucker: 0.9, mouthFunnel: 0.3 } }
    ];

    var RAW_GROUPS = [
        { key: 'eyes', rx: /^eye/i }, { key: 'brows', rx: /^brow/i }, { key: 'cheeks', rx: /^cheek/i },
        { key: 'nose', rx: /^nose/i }, { key: 'jaw', rx: /^jaw/i }, { key: 'mouth', rx: /^mouth/i },
        { key: 'tongue', rx: /^tongue/i }, { key: 'visemes', rx: /^viseme/i }
    ];

    function M() { return window.__taMorph; }
    function ready() { return M() && M().ready() && document.getElementById('tav-face-body'); }

    function apply() {
        var m = M(); if (!m) return;
        m.reset();
        for (var n in morphState) { if (morphState[n] > 0) m.set(n, morphState[n]); }
        m.setPose(pose.rx, pose.ry, pose.rz);
    }

    // read the current 0..1 value stored for a raw name
    function ms(name) { return morphState[name.toLowerCase()] || 0; }
    function setMs(name, v) { var k = name.toLowerCase(); if (v > 0) morphState[k] = v > 1 ? 1 : v; else delete morphState[k]; }
    function avg(names) { var s = 0; for (var i = 0; i < names.length; i++) s += ms(names[i]); return s / names.length; }

    // ── DOM helpers ──
    function el(tag, cls, txt) { var e = document.createElement(tag); if (cls) e.className = cls; if (txt != null) e.textContent = txt; return e; }
    function sliderRow(labelText, min, max, hintText) {
        var row = el('div', 'tav-fp-row');
        var lab = el('label', 'tav-fp-label');
        var name = el('span', 'tav-fp-name', labelText);
        lab.appendChild(name);
        if (hintText) { var h = el('span', 'tav-fp-hint', hintText); lab.appendChild(h); }
        var input = el('input', 'tav-fp-range'); input.type = 'range'; input.min = min; input.max = max; input.step = 1; input.value = 0;
        row.appendChild(lab); row.appendChild(input);
        return { row: row, input: input };
    }

    function makeUni(spec, labelText, hintText) {
        var r = sliderRow(labelText, 0, 100, hintText);
        var names = spec.names;
        function write() { var v = (+r.input.value) / 100; for (var i = 0; i < names.length; i++) setMs(names[i], v); apply(); syncSiblings(); }
        r.input.addEventListener('input', write);
        return { kind: 'uni', input: r.input, row: r.row, read: function () { r.input.value = Math.round(avg(names) * 100); } };
    }
    function makeBi(spec, labelText) {
        var r = sliderRow(labelText, -100, 100, null);
        function write() {
            var v = (+r.input.value) / 100;
            var pos = spec.pos, neg = spec.neg, i;
            if (v >= 0) { for (i = 0; i < pos.length; i++) setMs(pos[i], v); for (i = 0; i < neg.length; i++) setMs(neg[i], 0); }
            else { for (i = 0; i < neg.length; i++) setMs(neg[i], -v); for (i = 0; i < pos.length; i++) setMs(pos[i], 0); }
            apply(); syncSiblings();
        }
        r.input.addEventListener('input', write);
        return { kind: 'bi', input: r.input, row: r.row, read: function () { r.input.value = Math.round((avg(spec.pos) - avg(spec.neg)) * 100); } };
    }
    function makePose(axis, labelText) {
        var r = sliderRow(labelText, -100, 100, null);
        function write() { pose[axis] = ((+r.input.value) / 100) * POSE_MAX[axis]; apply(); }
        r.input.addEventListener('input', write);
        return { kind: 'pose', input: r.input, row: r.row, read: function () { r.input.value = Math.round((pose[axis] / POSE_MAX[axis]) * 100); } };
    }

    function syncSiblings() { for (var i = 0; i < controls.length; i++) controls[i].read(); }
    function syncUI() { syncSiblings(); }

    function groupBox(titleText) {
        var box = el('div', 'tav-fp-group');
        box.appendChild(el('div', 'tav-fp-gtitle', titleText));
        return box;
    }

    function build() {
        if (built) return; built = true;
        var body = document.getElementById('tav-face-body');
        if (!body) return;

        // 1) presets
        var pWrap = el('div', 'tav-fp-presets');
        PRESETS.forEach(function (p) {
            var b = el('button', 'tav-fp-preset'); b.type = 'button';
            b.appendChild(document.createTextNode(p.emoji + ' '));
            b.appendChild(el('span', null, t('presets.' + p.key, p.key)));
            b.addEventListener('click', function () {
                morphState = {}; pose = { rx: 0, ry: 0, rz: 0 };
                for (var name in p.m) setMs(name, p.m[name]);
                apply(); syncUI();
            });
            pWrap.appendChild(b);
        });
        var pLab = el('div', 'tav-fp-slabel', t('presets_label', 'Quick expressions'));
        body.appendChild(pLab); body.appendChild(pWrap);

        // 2) semantic sliders grouped
        var groupsWrap = el('div', 'tav-fp-groups');
        var byGroup = {};
        SEMANTIC.forEach(function (spec) {
            var g = spec.group;
            if (!byGroup[g]) { byGroup[g] = groupBox(t('groups.' + g, g)); groupsWrap.appendChild(byGroup[g]); }
            var labelText = t('ctl.' + spec.key, spec.key);
            var hintText = spec.hint ? t('tongue_hint', null) : null;
            var c = (spec.kind === 'bi') ? makeBi(spec, labelText) : makeUni(spec, labelText, hintText);
            controls.push(c); byGroup[g].appendChild(c.row);
        });
        // head pose group
        var poseBox = groupBox(t('groups.head', 'Head'));
        [['ry', 'head_yaw'], ['rx', 'head_pitch'], ['rz', 'head_roll']].forEach(function (a) {
            var c = makePose(a[0], t('ctl.' + a[1], a[1])); controls.push(c); poseBox.appendChild(c.row);
        });
        groupsWrap.appendChild(poseBox);
        body.appendChild(groupsWrap);

        // 3) advanced: every raw morph as its own slider (GNM-like granularity)
        var names = M().names();
        var adv = el('details', 'tav-fp-adv');
        var sum = el('summary', null, t('advanced', 'All parameters')); adv.appendChild(sum);
        var advBody = el('div', 'tav-fp-advbody');
        RAW_GROUPS.forEach(function (rg) {
            var list = names.filter(function (n) { return rg.rx.test(n); }).sort();
            if (!list.length) return;
            var box = groupBox(t('groups.' + rg.key, rg.key));
            list.forEach(function (n) {
                var spec = { names: [n] };
                var c = makeUni(spec, n, null);
                controls.push(c); box.appendChild(c.row);
            });
            advBody.appendChild(box);
        });
        adv.appendChild(advBody);
        body.appendChild(adv);

        // 4) footer controls: idle animation toggle + reset
        var foot = el('div', 'tav-fp-foot');
        var idleLab = el('label', 'tav-check');
        var idleCb = el('input'); idleCb.type = 'checkbox'; idleCb.checked = true;
        idleLab.appendChild(idleCb); idleLab.appendChild(el('span', null, t('idle', 'Live micro-motion (blink, breathing)')));
        idleCb.addEventListener('change', function () { try { M().idle(idleCb.checked); } catch (_) {} });
        var resetBtn = el('button', 'tav-fp-reset', t('reset', 'Reset face')); resetBtn.type = 'button';
        resetBtn.addEventListener('click', function () {
            morphState = {}; pose = { rx: 0, ry: 0, rz: 0 };
            apply(); syncUI();
            try { if (window.__ae) window.__ae('ta_face_reset', {}); } catch (_) {}
        });
        foot.appendChild(idleLab); foot.appendChild(resetBtn);
        body.appendChild(foot);

        syncUI();
        try { if (window.__ae) window.__ae('ta_face_panel_built', { controls: controls.length }); } catch (_) {}
    }

    // ── boot: expand/collapse toggle + build once the engine is ready ──
    function initToggle() {
        var toggle = document.getElementById('tav-face-toggle'), body = document.getElementById('tav-face-body');
        if (!toggle || !body) return;
        toggle.addEventListener('click', function () {
            var open = body.hasAttribute('hidden');
            if (open) { body.removeAttribute('hidden'); toggle.setAttribute('aria-expanded', 'true'); toggle.classList.add('open'); build(); }
            else { body.setAttribute('hidden', ''); toggle.setAttribute('aria-expanded', 'false'); toggle.classList.remove('open'); }
            try { if (window.__ae) window.__ae('ta_face_toggle', { open: open }); } catch (_) {}
        });
    }

    function boot() {
        initToggle();
        // If the panel gets opened before the avatar finished loading, build() bails and
        // this poll finishes the job once __taMorph is ready and the body is visible.
        var tries = 0, timer = setInterval(function () {
            tries++;
            var body = document.getElementById('tav-face-body');
            if (ready() && body && !body.hasAttribute('hidden') && !built) build();
            if (built || tries > 120) clearInterval(timer);
        }, 500);
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
})();
